import { RefreshCw, Search } from "lucide-react";

import Datatable from "@components/datatable";
import InvoiceCreateModal from "./invoice-create-modal";
import LoadingButton from "@components/ui-components/loading-button";
import Select from "@components/ui-components/select";
import useSuperadminInvoices from "../hooks/use-superadmin-invoices";
import {
    AFFILIATES_MAX_LIMIT,
    AFFILIATE_KYC_STATUS_OPTIONS,
    AFFILIATE_PAYOUT_STATUS_OPTIONS
} from "../misc/constants";
import { formatCurrency } from "@helpers/utils";
import { getAffiliateColumns } from "../misc/columns";

// Step 4: pick the brand — its id scopes the publisher list and its api_key
// signs the invoice call — then raise the invoice from a publisher's row.
const InvoicesPanel = () => {
    const {
        brandOptions, brandId, setBrandId, selectedBrand,
        rows, pagination, loading, filters,
        handleFilterChange, handlePageChange, handlePerRowsChange, refresh,
        modal, methods, openInvoice, closeInvoice, submitInvoice,
        raised, dismissRaised, regenerateInvoiceIds
    } = useSuperadminInvoices();

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Publisher invoices</h2>
                    <p className="text-sm text-muted">
                        {selectedBrand
                            ? `${pagination.total} publisher(s) on ${selectedBrand.name || selectedBrand.ext_id}.`
                            : "Pick a brand to list its publishers."}
                    </p>
                </div>
                <LoadingButton
                    color="neutral"
                    size="sm"
                    icon={RefreshCw}
                    text="Refresh"
                    disabled={!brandId}
                    onClick={refresh} />
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-end gap-4">
                    <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700">Brand</label>
                        <Select
                            id="invoice-brand"
                            className="w-[260px]"
                            placeholder="Pick a brand"
                            value={brandId}
                            options={brandOptions}
                            onChange={setBrandId} />
                    </div>
                    <span className="mb-2.5 text-xs text-muted">
                        {selectedBrand
                            ? <>
                                <code className="rounded bg-light px-1.5 py-0.5 text-gray-700">{selectedBrand._id}</code>
                                {" scopes the list · api_key "}
                                <code className="rounded bg-light px-1.5 py-0.5 text-gray-700">{`${String(selectedBrand.api_key || "").slice(0, 10)}…`}</code>
                                {" signs the invoice"}
                            </>
                            : "The brand's id scopes the publisher list; its api_key authenticates the invoice call."}
                    </span>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="relative max-w-sm flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                    <input
                        type="text"
                        value={filters.search}
                        disabled={!brandId}
                        onChange={(event) => handleFilterChange({ search: event.target.value })}
                        placeholder="Search name or email"
                        className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-light disabled:text-muted" />
                </div>
                <Select
                    id="invoice-kyc-filter"
                    className="w-[190px]"
                    placeholder="All KYC statuses"
                    value={filters.kyc_status}
                    options={AFFILIATE_KYC_STATUS_OPTIONS}
                    disabled={!brandId}
                    onChange={(value) => handleFilterChange({ kyc_status: value })} />
                <Select
                    id="invoice-payout-filter"
                    className="w-[180px]"
                    placeholder="All payout statuses"
                    value={filters.payout_status}
                    options={AFFILIATE_PAYOUT_STATUS_OPTIONS}
                    disabled={!brandId}
                    onChange={(value) => handleFilterChange({ payout_status: value })} />
            </div>

            {raised.length > 0 && (
                <div className="mt-4 rounded-xl border border-success/20 bg-success/5 p-4">
                    <div className="text-sm font-medium text-gray-900">Raised this session</div>
                    <ul className="mt-3 space-y-2">
                        {raised.map((invoice) => (
                            <li
                                key={invoice.id}
                                className="flex flex-wrap items-center gap-x-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                                <span className="font-medium text-gray-900">{invoice.number}</span>
                                <span className="text-muted">{invoice.publisher}</span>
                                <span className="ml-auto text-gray-700">{formatCurrency(invoice.amount, invoice.currency)}</span>
                                <button
                                    type="button"
                                    onClick={() => dismissRaised(invoice.id)}
                                    className="text-xs text-gray-400 hover:text-gray-700">
                                    Dismiss
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <Datatable
                    columns={getAffiliateColumns({ onCreateInvoice: openInvoice })}
                    data={rows}
                    loading={loading}
                    pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    paginationRowsPerPageOptions={[10, 20, 50, AFFILIATES_MAX_LIMIT]}
                    customNoData={
                        <span className="text-sm text-muted">
                            {brandId ? "No publishers on this brand." : "Pick a brand to see its publishers."}
                        </span>
                    } />
            </div>

            <InvoiceCreateModal
                modal={modal}
                brand={selectedBrand}
                methods={methods}
                loading={loading}
                onClose={closeInvoice}
                onSubmit={submitInvoice}
                onRegenerateIds={regenerateInvoiceIds} />
        </div>
    );
};

export default InvoicesPanel;
